/**
 * Session timeout cleanup utility
 *
 * This module provides a centralized function to clean up session refresh timeouts
 * to prevent memory leaks. It's separated from sessionUtility.js to avoid circular
 * dependencies with sessionSlice.
 */

// Extend Window interface for sessionRefreshTimeout property
declare global {
    interface Window {
        sessionRefreshTimeout?: ReturnType<typeof setTimeout>;
    }
}

/**
 * Cleans up the session refresh timeout to prevent memory leaks.
 *
 * This function should be called in all scenarios where the application
 * is being destroyed, navigated away from, or when session management
 * is no longer needed (logout, session expiration, etc.).
 *
 * Prevents memory leaks by:
 * - Clearing the active timeout to prevent future execution
 * - Removing the reference to allow garbage collection
 * - Preventing stale timeout callbacks from holding Redux dispatch references
 */
export const cleanupSessionRefreshTimeout = (): void => {
    const sessionTimeout = window.sessionRefreshTimeout;
    if (sessionTimeout) {
        clearTimeout(sessionTimeout);
        window.sessionRefreshTimeout = undefined;
    }
};
